import { useState } from "react";
import { useSound } from "@/context/SoundContext";
import { toast } from "sonner";
import { Volume2, VolumeX, Play, SkipForward, SkipBack, Sparkles, ListMusic, ChevronDown, ChevronUp, Check } from "lucide-react";

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="relative h-6 w-11 rounded-full transition-colors flex-shrink-0"
      style={{ backgroundColor: on ? "var(--sfx-accent)" : "#1a2a44" }}>
      <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all" style={{ left: on ? "22px" : "2px" }} />
    </button>
  );
}

const loadCollapsed = () => { try { return JSON.parse(localStorage.getItem("sfx_ai_sound_collapsed")) ?? false; } catch { return false; } };

export const AISoundCard = () => {
  const s = useSound();
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  if (!s) return null;
  const { aiSoundOn, setAiSoundOn, autoMode, setAutoMode, currentSound, preview, next, prev, setPlayerOpen, persistSound } = s;

  const setCollapsedPersist = (v) => {
    setCollapsed(v);
    try { localStorage.setItem("sfx_ai_sound_collapsed", JSON.stringify(v)); } catch { /* ignore */ }
  };

  const save = async () => {
    const ok = await persistSound();
    setCollapsedPersist(true);
    toast.success(ok
      ? `Winning sound saved to your account: ${currentSound?.name}`
      : `Winning sound set: ${currentSound?.name}`);
  };

  // Collapsed: compact bar that saves space; tap to re-open.
  if (collapsed) {
    return (
      <button onClick={() => setCollapsedPersist(false)} data-testid="ai-sound-card-collapsed"
        className="w-full border border-[#14233d] bg-[#0A1120] rounded-2xl p-4 mt-6 flex items-center gap-3 text-left hover:border-[#24406b] transition-colors">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
          {aiSoundOn ? <Volume2 className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} /> : <VolumeX className="h-4 w-4 text-[#5f7ba6]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-semibold">Trade Alerts</div>
          <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] truncate">{currentSound?.name}</div>
        </div>
        <ChevronDown className="h-4 w-4 text-[#5f7ba6]" />
      </button>
    );
  }

  return (
    <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl p-5 mt-6" data-testid="ai-sound-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {aiSoundOn ? <Volume2 className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} /> : <VolumeX className="h-4 w-4 text-[#5f7ba6]" />}
          <span className="font-heading font-semibold">Trade Alerts</span>
        </div>
        <div className="flex items-center gap-3">
          <Toggle on={aiSoundOn} onClick={() => setAiSoundOn(!aiSoundOn)} testid="ai-sound-toggle" />
          <button onClick={() => setCollapsedPersist(true)} data-testid="ai-sound-collapse" className="text-[#5f7ba6] hover:text-white">
            <ChevronUp className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="text-xs text-[#5f7ba6] mb-4">Celebration sounds the Plays automatically for your winning trades.</p>

      <div className="border border-[#14233d] bg-[#060c18] rounded-2xl p-4">
        <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Current Sound</div>
        <div className="font-heading text-base font-bold mt-0.5 mb-3" data-testid="ai-sound-current">{currentSound?.name}</div>
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="ai-sound-prev"
            className="h-10 w-10 rounded-xl border border-[#14233d] flex items-center justify-center text-[#8fb4e6] hover:border-[#24406b]">
            <SkipBack className="h-4 w-4" />
          </button>
          <button onClick={() => preview()} data-testid="ai-sound-preview"
            className="flex-1 h-10 rounded-xl flex items-center justify-center gap-2 text-white font-semibold text-sm hover:opacity-90"
            style={{ backgroundColor: "var(--sfx-accent)" }}>
            <Play className="h-4 w-4" fill="white" /> Play Preview
          </button>
          <button onClick={next} data-testid="ai-sound-next"
            className="h-10 w-10 rounded-xl border border-[#14233d] flex items-center justify-center text-[#8fb4e6] hover:border-[#24406b]">
            <SkipForward className="h-4 w-4" />
          </button>
        </div>
        <button onClick={() => setPlayerOpen(true)} data-testid="ai-sound-open-player"
          className="mt-3 w-full flex items-center justify-center gap-2 text-xs text-[#8fb4e6] hover:text-white py-2">
          <ListMusic className="h-3.5 w-3.5" /> Open music player
        </button>
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#14233d]">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
          <div>
            <div className="font-heading text-sm font-semibold">Auto Alert Mode</div>
            <div className="text-[11px] text-[#5f7ba6]">Plays a sound automatically on wins, profit targets & milestones.</div>
          </div>
        </div>
        <Toggle on={autoMode} onClick={() => setAutoMode(!autoMode)} testid="ai-auto-sound-toggle" />
      </div>

      <button onClick={save} data-testid="ai-sound-save"
        className="mt-4 w-full h-11 rounded-full flex items-center justify-center gap-2 text-white font-semibold text-sm hover:opacity-90 transition-opacity"
        style={{ backgroundColor: "var(--sfx-accent)" }}>
        <Check className="h-4 w-4" /> Save & Close
      </button>
    </div>
  );
};
